import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Spinner, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { bikes, BikletaLogoComplete } from '../../assets';
import './styles.css';

const USER_QR_URL = `${process.env.REACT_APP_API_URL_LATEST}/api/auth/user-qrs/code`;
const BIKE_QR_URL = `${process.env.REACT_APP_API_URL_LATEST}/api/bikes/bike-qrs/code`;
const QR_API_KEY = process.env.REACT_APP_BASIC_API_KEY ?? '';

type QrKind = 'user' | 'bike';

interface Phone {
  prefix?: string;
  number?: string;
}

interface UserQrDetails {
  user?: {
    firstName?: string;
    lastName?: string;
    idNumber?: string;
  };
  contacts?: {
    name?: string;
    relationship?: string;
    phone?: Phone;
  }[];
  health?: {
    allergies?: string;
    bloodType?: string;
    conditions?: string[];
    height?: number;
    weight?: number;
    medicalCoverage?: string;
    medication?: string;
  };
}

interface BikeQrDetails {
  name?: string;
  brandName?: string;
  modelName?: string;
  serialNumber?: string;
  year?: number;
  type?: string;
  frameSize?: string;
  wheelSize?: string;
  color?: string;
}

interface ApiResponse<T> {
  data: T;
}

const emptyValue = 'Sin completar';

const conditionLabels: Record<string, string> = {
  hypertension: 'Hipertensión',
  diabetes_type_1: 'Diabetes tipo 1',
  diabetes_type_2: 'Diabetes tipo 2',
  asthma: 'Asma',
  epilepsy: 'Epilepsia',
  heart_disease: 'Enfermedad cardíaca',
  severe_allergy: 'Alergia severa',
  celiac: 'Celiaquía',
  hypothyroidism: 'Hipotiroidismo',
};

const relationshipLabels: Record<string, string> = {
  partner: 'Pareja',
  parent: 'Padre/Madre',
  sibling: 'Hermano/a',
  child: 'Hijo/a',
  friend: 'Amigo/a',
  doctor: 'Doctor/a',
  other: 'Otro',
};

const bikeTypeLabels: Record<string, string> = {
  bmx: 'BMX',
  ciclocross: 'Ciclocross',
  mountain: 'Mountain Bike',
  child: 'Infantil',
  cruiser: 'Playera',
  route: 'Ruta',
  urban: 'Urbana',
  electricUrban: 'Urbana eléctrica',
  electricRoute: 'Ruta eléctrica',
  electricMountain: 'Mountain Bike eléctrica',
  electricScooter: 'Monopatín eléctrico',
  foldable: 'Plegable',
};

const getValue = (value?: string | number) =>
  value === undefined || value === null || value === '' ? emptyValue : String(value);

const formatMeasure = (value?: number, suffix?: string) =>
  value === undefined || value === null ? emptyValue : `${value} ${suffix}`;

const formatPhone = (phone?: Phone) => {
  if (!phone?.prefix && !phone?.number) return emptyValue;
  return `${phone?.prefix || ''} ${phone?.number || ''}`.trim();
};

const formatWheelSize = (value?: string) => {
  if (!value) return emptyValue;
  return value.includes('"') ? value : `${value}"`;
};

const normalizeQrKind = (code?: string): QrKind | null => {
  const prefix = code?.toLowerCase().slice(0, 3);
  if (prefix === 'bku') return 'user';
  if (prefix === 'bkb') return 'bike';
  return null;
};

const InvalidQrIcon = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="18" height="18" rx="3" stroke="#202124" strokeWidth="2.4" />
    <rect x="9" y="9" width="8" height="8" rx="1.5" fill="#202124" />
    <rect x="30" y="4" width="18" height="18" rx="3" stroke="#202124" strokeWidth="2.4" />
    <rect x="35" y="9" width="8" height="8" rx="1.5" fill="#202124" />
    <rect x="4" y="30" width="18" height="18" rx="3" stroke="#202124" strokeWidth="2.4" />
    <rect x="9" y="35" width="8" height="8" rx="1.5" fill="#202124" />
    <line x1="31" y1="31" x2="48" y2="48" stroke="#E53E3E" strokeWidth="3.5" strokeLinecap="round" />
    <line x1="48" y1="31" x2="31" y2="48" stroke="#E53E3E" strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

const NetworkErrorIcon = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26 10L47 44H5L26 10Z" stroke="#202124" strokeWidth="2.4" strokeLinejoin="round" />
    <line x1="26" y1="24" x2="26" y2="35" stroke="#202124" strokeWidth="2.8" strokeLinecap="round" />
    <circle cx="26" cy="40" r="2" fill="#202124" />
  </svg>
);

const ErrorState = ({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) => {
  const isInvalidQr = error === 'QR inválido';
  const isNotFound = error === 'QR no encontrado';

  const icon = isInvalidQr || isNotFound ? <InvalidQrIcon /> : <NetworkErrorIcon />;

  const heading = isInvalidQr
    ? 'QR no válido'
    : isNotFound
    ? 'Código no encontrado'
    : 'No pudimos cargar los datos';

  const description = isInvalidQr
    ? 'El código escaneado no corresponde a un QR de Bikleta. Verificá que el QR sea correcto e intentá de nuevo.'
    : isNotFound
    ? 'No encontramos información asociada a este QR. Intentá escanear el código nuevamente o verificá que el código sea correcto.'
    : 'Tuvimos un problema al obtener la información. Revisá tu conexión e intentá de nuevo.';

  return (
    <div className="qr-error-card">
      <div className="qr-error-icon-wrap">
        {icon}
      </div>
      <h2 className="qr-error-heading">{heading}</h2>
      <p className="qr-error-desc">{description}</p>
      {!isInvalidQr && (
        <button className="qr-error-retry" onClick={onRetry}>
          Reintentar
        </button>
      )}
      <div className="qr-error-brand">
        <img src={BikletaLogoComplete.dark} alt="Bikleta" className="qr-error-brand-logo" />
      </div>
    </div>
  );
};

const DetailRow = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string | number;
  children?: React.ReactNode;
}) => (
  <div className="qr-row">
    <span className="qr-label">{label}</span>
    {children || <span className="qr-value">{getValue(value)}</span>}
  </div>
);

const UserDetails = ({ data }: { data: UserQrDetails }) => {
  const conditions = data.health?.conditions || [];
  const contacts = data.contacts?.length ? data.contacts : [{}];

  return (
    <>
      <section className="qr-card">
        <DetailRow label="Nombre" value={data.user?.firstName} />
        <DetailRow label="Apellido" value={data.user?.lastName} />
        <DetailRow label="DNI" value={data.user?.idNumber} />
        <DetailRow label="Alergias" value={data.health?.allergies} />
        <DetailRow label="Grupo sanguíneo" value={data.health?.bloodType} />
        <DetailRow label="Altura" value={formatMeasure(data.health?.height, 'cm')} />
        <DetailRow label="Peso" value={formatMeasure(data.health?.weight, 'kg')} />
        <DetailRow label="Cobertura médica" value={data.health?.medicalCoverage} />
        <DetailRow label="Medicación" value={data.health?.medication} />
        <div className="qr-conditions">
          <span className="qr-label">Condiciones</span>
          {conditions.length ? (
            <div className="qr-chip-list">
              {conditions.map(condition => (
                <span className="qr-chip" key={condition}>
                  {conditionLabels[condition] || condition}
                </span>
              ))}
            </div>
          ) : (
            <span className="qr-value qr-empty-inline">{emptyValue}</span>
          )}
        </div>
        <div className="qr-contacts">
          <Text className="qr-contacts-title">Contactos de emergencia</Text>
          {contacts.map((contact, index) => (
            <div className="qr-contact" key={`${contact.name || 'contact'}-${index}`}>
              <Text className="qr-contact-title">Contacto {index + 1}</Text>
              <DetailRow label="Nombre" value={contact.name} />
              <DetailRow
                label="Relación"
                value={
                  contact.relationship
                    ? relationshipLabels[contact.relationship] || contact.relationship
                    : emptyValue
                }
              />
              <DetailRow label="Teléfono" value={formatPhone(contact.phone)} />
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

const BikeDetails = ({ data }: { data: BikeQrDetails }) => {
  const bikeAsset = data.type ? bikes[data.type]?.image : undefined;

  return (
    <>
      {bikeAsset && (
        <img className="qr-bike-image" src={bikeAsset} alt={getValue(data.name)} />
      )}
      <section className="qr-card">
        <DetailRow label="Nombre" value={data.name} />
        <DetailRow label="Marca" value={data.brandName} />
        <DetailRow label="Modelo" value={data.modelName} />
        <DetailRow label="Nro. de serie" value={data.serialNumber} />
        <DetailRow label="Año" value={data.year} />
        <DetailRow
          label="Tipo"
          value={data.type ? bikeTypeLabels[data.type] || data.type : emptyValue}
        />
        <DetailRow label="Talle" value={data.frameSize} />
        <DetailRow label="Rodado" value={formatWheelSize(data.wheelSize)} />
        <DetailRow label="Color">
          <span className="qr-color-value">
            {data.color && (
              <span
                className="qr-color-swatch"
                style={{ backgroundColor: data.color }}
              />
            )}
            <span>{getValue(data.color)}</span>
          </span>
        </DetailRow>
      </section>
    </>
  );
};

const QRView = () => {
  const { qrValue } = useParams();
  const [kind, setKind] = useState<QrKind | null>(null);
  const [userData, setUserData] = useState<UserQrDetails | null>(null);
  const [bikeData, setBikeData] = useState<BikeQrDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const retry = useCallback(() => setRetryCount(c => c + 1), []);

  const title = useMemo(() => {
    if (kind === 'bike') return bikeData?.name || 'QR Bikleta';
    if (kind === 'user') return 'QR Personal';
    return 'QR';
  }, [bikeData?.name, kind]);

  useEffect(() => {
    const loadQrDetails = async () => {
      setIsLoading(true);
      setError('');
      setUserData(null);
      setBikeData(null);

      const qrKind = normalizeQrKind(qrValue);
      setKind(qrKind);

      if (!qrValue || !qrKind) {
        setError('QR inválido');
        setIsLoading(false);
        return;
      }

      try {
        const url = qrKind === 'user' ? USER_QR_URL : BIKE_QR_URL;
        const response = await axios.get<ApiResponse<UserQrDetails | BikeQrDetails>>(
          `${url}/${encodeURIComponent(qrValue)}`,
          {
            headers: {
              'x-basic-api-key': QR_API_KEY,
            },
          }
        );
        if (response.data.data === null || response.data.data === undefined) {
          setError('QR no encontrado');
          return;
        }

        if (qrKind === 'user') {
          setUserData(response.data.data as UserQrDetails);
        } else {
          setBikeData(response.data.data as BikeQrDetails);
        }
      } catch (err) {
        setError('No pudimos cargar los datos del QR');
      } finally {
        setIsLoading(false);
      }
    };

    loadQrDetails();
  }, [qrValue, retryCount]);

  return (
    <Box className="qr-page">
      <header className="qr-header">
        <Text className="qr-title">{title}</Text>
      </header>

      {isLoading && (
        <div className="qr-state">
          <Spinner thickness="4px" speed="0.65s" color="#F7D42D" size="xl" />
        </div>
      )}

      {!isLoading && error && (
        <div className="qr-state">
          <ErrorState error={error} onRetry={retry} />
        </div>
      )}

      {!isLoading && !error && kind === 'user' && userData && (
        <UserDetails data={userData} />
      )}

      {!isLoading && !error && kind === 'bike' && bikeData && (
        <BikeDetails data={bikeData} />
      )}
    </Box>
  );
};

export default QRView;
